/**
 * AuthMiddleware
 * 
 * Service for handling authentication flows, particularly password reset
 * Provides middleware functions to ensure tokens are properly maintained
 */

import { parseRecoveryTokensFromUrl, validateJwtToken } from '@/utils/auth-utils';

export class AuthMiddlewareService {
  private static instance: AuthMiddlewareService;
  
  // Singleton access
  public static getInstance(): AuthMiddlewareService {
    if (!AuthMiddlewareService.instance) {
      AuthMiddlewareService.instance = new AuthMiddlewareService();
    }
    return AuthMiddlewareService.instance;
  }
  
  /**
   * Captures recovery tokens from URL and stores them for the password reset flow
   * This should be called as early as possible on app load
   */
  public captureRecoveryToken(url: string = window.location.href): boolean {
    try {
      console.log('🔍 AuthMiddleware - Checking URL for recovery tokens');
      
      // Parse the URL for tokens
      const { accessToken, refreshToken, type } = parseRecoveryTokensFromUrl(url);
      
      if (accessToken && type === 'recovery') {
        console.log('✅ AuthMiddleware - Found recovery token in URL');
        
        // Validate token
        const validation = validateJwtToken(accessToken);
        if (!validation.valid) {
          console.error('❌ AuthMiddleware - Token validation failed:', validation.error);
          // Store anyway - API might still accept it
        }
        
        // Store in both localStorage and sessionStorage
        localStorage.setItem('recovery_access_token', accessToken);
        sessionStorage.setItem('recovery_access_token', accessToken);
        
        if (refreshToken) {
          localStorage.setItem('recovery_refresh_token', refreshToken);
          sessionStorage.setItem('recovery_refresh_token', refreshToken);
        }
        
        // Store even more aggressively in cookies for extra redundancy
        try {
          document.cookie = `recovery_access_token=${accessToken}; path=/; max-age=3600; SameSite=Strict`;
          if (refreshToken) {
            document.cookie = `recovery_refresh_token=${refreshToken}; path=/; max-age=3600; SameSite=Strict`;
          }
        } catch (e) {
          console.error('Failed to store in cookies:', e);
        }
        
        console.log('✅ AuthMiddleware - Successfully stored tokens');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ AuthMiddleware - Error capturing token:', error);
      return false;
    }
  }
  
  /**
   * Check if we are currently in a password reset flow
   */
  public isInPasswordResetFlow(): boolean {
    // Check URL for reset indicators
    const url = window.location.href;
    const isResetPage = url.includes('/reset-password');
    const hasRecoveryToken = url.includes('type=recovery');
    const hasCodeParam = new URLSearchParams(window.location.search).has('code');
    
    // Check storage for tokens
    const hasStoredToken = !!localStorage.getItem('recovery_access_token') || 
                          !!sessionStorage.getItem('recovery_access_token');
    
    // Check cookies as last resort
    const hasCookieToken = document.cookie.includes('recovery_access_token');
    
    return isResetPage || hasRecoveryToken || hasCodeParam || hasStoredToken || hasCookieToken;
  }
  
  /**
   * Ensures we're on the password reset page if we have a token
   * Redirects if needed
   */
  public ensurePasswordResetPage(): void {
    const isResetPage = window.location.pathname.includes('/reset-password');
    
    // If we're already on reset page, don't do anything
    if (isResetPage) {
      console.log('✅ AuthMiddleware - Already on reset password page');
      return;
    }
    
    // If we're not on the reset page but have recovery indicators, redirect
    if (this.isInPasswordResetFlow()) {
      console.log('⚠️ AuthMiddleware - Not on reset page but in reset flow, redirecting');
      window.location.href = `${window.location.origin}/reset-password`;
    }
  }
  
  /**
   * Check if we should prevent redirect away from reset password page
   * This helps when we're already on the reset page and need to stay there
   */
  public shouldStayOnResetPage(): boolean {
    const isResetPage = window.location.pathname.includes('/reset-password');
    
    // If we're on the reset page and have token indicators, we should stay
    if (isResetPage) {
      const hasToken = !!localStorage.getItem('recovery_access_token') || 
                       !!sessionStorage.getItem('recovery_access_token') ||
                       document.cookie.includes('recovery_access_token');
      
      if (hasToken) {
        console.log('🛡️ AuthMiddleware - Should stay on reset password page');
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Get recovery tokens from all possible sources
   */
  public getRecoveryTokens(): { accessToken: string | null, refreshToken: string | null } {
    // Check localStorage first
    let accessToken = localStorage.getItem('recovery_access_token');
    let refreshToken = localStorage.getItem('recovery_refresh_token');
    
    // Check sessionStorage if not found
    if (!accessToken) {
      accessToken = sessionStorage.getItem('recovery_access_token');
      refreshToken = sessionStorage.getItem('recovery_refresh_token');
    }
    
    // Check cookies as last resort
    if (!accessToken) {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'recovery_access_token') {
          accessToken = value;
        } else if (name === 'recovery_refresh_token') {
          refreshToken = value;
        }
      }
    }
    
    // Parse URL if no token found yet
    if (!accessToken) {
      const { accessToken: urlToken, refreshToken: urlRefreshToken } = parseRecoveryTokensFromUrl(window.location.href);
      accessToken = urlToken;
      refreshToken = urlRefreshToken;
    }
    
    return { accessToken, refreshToken };
  }
  
  /**
   * Clear all recovery tokens from all storage mechanisms
   */
  public clearRecoveryTokens(): void {
    // Clear localStorage
    localStorage.removeItem('recovery_access_token');
    localStorage.removeItem('recovery_refresh_token');
    
    // Clear sessionStorage
    sessionStorage.removeItem('recovery_access_token');
    sessionStorage.removeItem('recovery_refresh_token');
    
    // Clear cookies
    document.cookie = 'recovery_access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'recovery_refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    
    console.log('✅ AuthMiddleware - Cleared all recovery tokens');
  }
}

// Export singleton instance
export const authMiddleware = AuthMiddlewareService.getInstance(); 